import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import styles from './AppDataFetcher.module.css';

const token = localStorage.getItem("token");
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// Toggle this to true if you want the uploader UI to also accept
// `full_name` and `image` columns and forward them as `metadata`.
// When false, only `uid` is read (matches current behaviour).
const ENABLE_METADATA_UPLOAD = false;

const AppDataFetcher = ({ appData, tenantPlan }) => {
  const [apiResponse, setApiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fetchUsersList = async () => {
    try {
      setLoading(true);
      const requestBody = {
        app_names: [{
          app_name: appData?.app_name,
          version: appData.settings?.version,
          feature_live_enabled: appData.feature?.live_status?.enable,
          session_enabled: appData?.session_enabled
        }]
      };

      const response = await fetch(
        `https://${appData.region.toLowerCase()}.userauth2.${backend_api_base_url}/users_list`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setApiResponse(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersList();
  }, [appData]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setParsedData(null);

    try {
      // Parse Excel/CSV file immediately to validate and show preview
      const data = await readExcelFile(file);
      console.log('Parsed data:', data);

      // Prepare users data for preview
      const users = prepareUsersData(data);

      if (users.length === 0) {
        throw new Error('No valid users found in the file. Please ensure at least one row has a "uid" column.');
      }

      setSelectedFile(file);
      setParsedData(users);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setParsedData(null);
      event.target.value = ''; // Reset file input
    }
  };

  const handleSubmitUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      setError('No valid users to upload');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Send to bulk insert API
      const response = await sendBulkInsertRequest(parsedData);

      // Refresh the users list after successful upload
      await fetchUsersList();

      // Show success message in the response format
      setUploadResult(response);
      setShowUploader(false);
      setSelectedFile(null);
      setParsedData(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          resolve(jsonData);
        } catch (err) {
          reject(new Error('Failed to parse file: ' + err.message));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const prepareUsersData = (excelData) => {
    const users = [];

    // FIX 1: Do not silently default the version. If settings.version is
    // missing, we cannot decide which insert path the backend will take,
    // so bail out rather than mislabel rows as V3.
    const version = appData.settings?.version;
    if (!version) {
      throw new Error("App version is missing — cannot determine insert version.");
    }

    const appName = appData?.app_name;

    excelData.forEach((row, index) => {
      // Support different column name variations
      const uid = row.uid || row.UID || row.user_id || row.userId || row.id;

      if (!uid) {
        console.warn(`Row ${index + 1}: Missing uid, skipping`);
        return;
      }

      const userData = {
        uid: String(uid).trim()
      };

      // Optional metadata support (V2/V3 only — the backend ignores it for V4)
      if (ENABLE_METADATA_UPLOAD) {
        const rawFullName = row.full_name || row.name || row.Name || row.FullName;
        const rawImage    = row.image     || row.Image || row.avatar || row.Avatar;

        const metadata = {};
        if (rawFullName !== undefined && rawFullName !== null && String(rawFullName).trim() !== '') {
          metadata.full_name = String(rawFullName).trim();
        }
        if (rawImage !== undefined && rawImage !== null && String(rawImage).trim() !== '') {
          metadata.image = String(rawImage).trim();
        }

        if (Object.keys(metadata).length > 0) {
          userData.metadata = metadata;
        }
      }

      // Create user entry with only the fields the backend needs
      const userEntry = {
        version: version,
        app_name: appName,
        user_data: userData
      };

      users.push(userEntry);
    });

    return users;
  };

  const sendBulkInsertRequest = async (users) => {
    // FIX 2: Rename for readability. In AppsData, `tenant_id` holds the
    // tenant slug (used as the `tenant` partition key on
    // OnboardedUserAuthAuthData), and `tenant_info.id` holds the
    // "TEN-XXXXX" style identifier.
    const tenantSlug = appData?.tenant_id;
    const tenantId   = appData?.tenant_info?.id;

    // Construct payload with both tenant and tenant_id
    const payload = {
      tenant: tenantSlug,     // REQUIRED - the tenant name/slug
      tenant_id: tenantId,    // REQUIRED - the tenant ID
      users: users
    };

    console.log('Sending bulk insert payload:', payload);

    const response = await fetch(
      `https://${appData.region.toLowerCase()}.userauth2.${backend_api_base_url}/bulk_users_insert`,
      {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      let errorData = null;
      try {
        errorData = await response.json();
      } catch (_) {
        // Non-JSON error body (e.g. API Gateway 502 HTML) — fall through
      }
      throw new Error(
        (errorData && errorData.error) || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  };

  const downloadTemplate = () => {
    // When metadata upload is enabled, expose the two optional columns
    // so users know they can fill them in.
    const template = ENABLE_METADATA_UPLOAD
      ? [
          { uid: 'user1', full_name: 'Alice', image: 'https://example.com/alice.png' },
          { uid: 'user2', full_name: 'Bob',   image: '' },
          { uid: 'user3', full_name: 'Carol', image: '' }
        ]
      : [
          { uid: 'user1' },
          { uid: 'user2' },
          { uid: 'user3' }
        ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users Template');
    XLSX.writeFile(wb, 'users_template.xlsx');
  };

  const cancelUpload = () => {
    setShowUploader(false);
    setSelectedFile(null);
    setParsedData(null);
    setError(null);
  };

  const renderExistingUsers = () => {
    const version = appData.settings?.version || 'V3';
    const isV1 = version === 'V1';

    if (!apiResponse || apiResponse.length === 0) {
      return (
        <div className={styles.emptyState}>
          No users found for this app.
        </div>
      );
    }

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.userTable}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell}>
                UID / User ID
              </th>
              <th className={styles.tableHeaderCell}>
                Version
              </th>
              {isV1 && (
                <>
                  <th className={styles.tableHeaderCell}>
                    Full Name
                  </th>
                  <th className={styles.tableHeaderCell}>
                    Email
                  </th>
                  <th className={styles.tableHeaderCell}>
                    Verified
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {apiResponse.map((user, idx) => {
              const userId = user.id || user.uid;
              const userVersion = user.app_version || user.version;

              return (
                <tr key={idx} className={styles.tableRow}>
                  <td className={styles.tableCell}>
                    {userId}
                  </td>
                  <td className={styles.tableCellSecondary}>
                    {userVersion || '-'}
                  </td>
                  {isV1 && (
                    <>
                      <td className={styles.tableCellSecondary}>
                        {user.full_name || '-'}
                      </td>
                      <td className={styles.tableCellSecondary}>
                        {user.email || '-'}
                      </td>
                      <td className={styles.tableCell}>
                        {user.is_email_verified !== undefined ? (
                          <span className={user.is_email_verified ? styles.badgeSuccess : styles.badgeError}>
                            {user.is_email_verified ? 'Verified' : 'Not Verified'}
                          </span>
                        ) : (
                          <span className={styles.badgeNeutral}>-</span>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderUploadResult = () => {
    if (!uploadResult) return null;

    return (
      <div className={styles.uploadResultContainer}>
        <div className={styles.resultsGrid}>
          <div className={styles.resultCardSuccess}>
            <div className={styles.resultNumberSuccess}>{uploadResult.success_count}</div>
            <div className={styles.resultLabelSuccess}>Successfully Inserted</div>
          </div>
          <div className={styles.resultCardError}>
            <div className={styles.resultNumberError}>{uploadResult.failed_count}</div>
            <div className={styles.resultLabelError}>Failed</div>
          </div>
        </div>

        {uploadResult.message && (
          <div className={styles.infoMessage}>
            {uploadResult.message}
          </div>
        )}

        {uploadResult.success_users && uploadResult.success_users.length > 0 && (
          <div className={styles.listContainer}>
            <h4 className={styles.sectionTitle}>Successfully Added Users</h4>
            <div className={styles.scrollableList}>
              <div className={styles.listContainer}>
                {uploadResult.success_users.map((user, idx) => (
                  <div key={idx} className={styles.listItem}>
                    • {user.uid} (v{user.version})
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {uploadResult.failed_users && uploadResult.failed_users.length > 0 && (
          <div>
            <h4 className={styles.sectionTitle}>Failed Users</h4>
            <div className={styles.scrollableList}>
              {uploadResult.failed_users.map((user, idx) => (
                <div key={idx} className={styles.listItemError}>
                  <div className={styles.errorUid}>UID: {user.uid}</div>
                  <div className={styles.errorReason}>Reason: {user.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => setUploadResult(null)}
          className={styles.buttonPrimary}
        >
          Dismiss Results
        </button>
      </div>
    );
  };

  const renderUploader = () => {
    if (!showUploader) return null;

    return (
      <div className={styles.uploader}>
        <div className={styles.uploaderHeader}>
          <h3 className={styles.uploaderTitle}>
            Bulk Upload Users
          </h3>
          <button
            onClick={cancelUpload}
            className={styles.closeButton}
            aria-label="Close uploader"
          >
            ×
          </button>
        </div>

        <p className={styles.uploaderDescription}>
          Upload an Excel (.xlsx, .xls) or CSV file with a list of user UIDs.
          <br />
          <span className={styles.uploaderHighlight}>
            Required column: <strong>uid</strong>
            {ENABLE_METADATA_UPLOAD && (
              <> &nbsp;· Optional columns: <strong>full_name</strong>, <strong>image</strong></>
            )}
          </span>
        </p>

        <div className={styles.uploaderActions}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            disabled={isUploading}
            className={styles.fileInput}
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className={`${styles.fileLabel} ${isUploading ? styles.fileLabelDisabled : ''}`}
          >
            {isUploading ? 'Processing...' : 'Choose File'}
          </label>

          <button
            onClick={downloadTemplate}
            className={styles.buttonOutline}
            disabled={isUploading}
          >
            Download Template
          </button>
        </div>

        {selectedFile && parsedData && (
          <div className={styles.filePreview}>
            <div className={styles.fileInfo}>
              <div className={styles.fileName}>
                <strong>Selected file:</strong> {selectedFile.name}
              </div>
              <div className={styles.fileStats}>
                <span>Total users: <strong>{parsedData.length}</strong></span>
                {parsedData.length > 0 && (
                  <span className={styles.previewUsers}>
                    First few: {parsedData.slice(0, 3).map(u => u.user_data.uid).join(', ')}
                    {parsedData.length > 3 && '...'}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.uploadActions}>
              <button
                onClick={handleSubmitUpload}
                disabled={isUploading}
                className={styles.submitButton}
              >
                {isUploading ? 'Uploading...' : 'Submit Upload'}
              </button>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setParsedData(null);
                  document.getElementById('file-upload').value = '';
                }}
                disabled={isUploading}
                className={styles.clearButton}
              >
                Clear File
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const version = appData.settings?.version || 'V3';
  const isV1 = version === 'V1';

  if (loading && !apiResponse && !uploadResult) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Loading users...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && (
        <div className={styles.errorMessage}>
          <strong>Error:</strong> {error}
          <button
            onClick={() => setError(null)}
            className={styles.closeErrorButton}
            aria-label="Close error"
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.header}>
        <h2 className={styles.title}>
          Users ({appData?.app_name})
        </h2>

        <div className={styles.buttonGroup}>
          {!isV1 && !showUploader && (
            <button
              onClick={() => {
                setShowUploader(true);
                setUploadResult(null);
              }}
              className={styles.buttonPrimary}
            >
              Bulk Upload Users
            </button>
          )}

          <button
            onClick={() => {
              fetchUsersList();
              setUploadResult(null);
            }}
            className={styles.buttonSecondary}
            disabled={loading || isUploading}
          >
            {loading ? 'Refreshing...' : 'Refresh List'}
          </button>
        </div>
      </div>

      {!isV1 && renderUploader()}

      {uploadResult && renderUploadResult()}

      {!uploadResult && (
        <div className={styles.tableWrapper}>
          {renderExistingUsers()}
        </div>
      )}
    </div>
  );
};

export default AppDataFetcher;